import React, { useEffect, useMemo, useState } from "react";
import TrialDetailsSection from "./TrialDetailsSection";
import StudyDetailsSection from "./StudyDetailsSection";
import CustomButton from "../../../components/@extended/CustomButton";
import TrialAccountDetailsSection from "./TrialAccountDetailsSection";
import TrialBudgetSection from "./TrialBudgetSection";
import { Box, Button, Grid } from "@mui/material";
import { useFormik, Formik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import DocumentsSection from "../DocumentsSection";
import * as Yup from "yup";
import {
  bloqcibeApi,
  useCreateTrialMutation,
  useCreateTrialPatchMutation,
  useCreateTrialPatchV2Mutation,
  useCreateTrialV2Mutation,
  useUpdateTrialPatchMutation,
} from "../../../store/slices/apiSlice";
import { useDispatch, useSelector } from "react-redux";
import FormikErrorFocus from "formik-error-focus";
import ConfirmationDialog from "../../../components/common/ConfirmationDialog";
import { openMessage } from "../../../store/slices/showMessageSlice";
import moment from "moment";

const testArticles = {
  id: "",
  drugName: "",
  drugClass: "",
  dosage: "",
  dosageUnit: "",
  routesOfAdministration: "",
  frequencyOfAdministration: "",
};

const otherCost = {
  id: "",
  cost: "",
  comment: "",
};

const BasicDetailsTab = ({ handleTabChange, isValidTab }) => {
  const navigate = useNavigate();
  const [fieldsVisible, setFieldsVisible] = useState(false);
  const [message, setMessage] = React.useState("");
  const [createTrial] = useCreateTrialMutation();
  const [createTrialPatch] = useCreateTrialPatchMutation();
  const [createTrialV2] = useCreateTrialV2Mutation();
  const [createTrialPatchV2] = useCreateTrialPatchV2Mutation();
  const [updateTrialPatch] = useUpdateTrialPatchMutation();
  const theraeupticAgents = { id: "", agentName: "", agentClass: "" };
  let { trialId } = useParams();
  const dispatch = useDispatch();
  const sponsorId = useSelector((state) => state.auth.sponsorId);
  // const [getTrialDetails, { data: trialDetails }] =
  //   bloqcibeApi.endpoints.getTrialDetails.useLazyQuery();
  const [getTrialDetailsV2, { data: trialDetailsV2 }] =
    bloqcibeApi.endpoints.getTrialDetailsV2.useLazyQuery();
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: "",
    buttonLabel: "Save",
  });

  const [cancelDialog, setCancelDialog] = useState({
    open: false,
    message: "Are you sure you want to cancel ?",
    buttonLabel: "OK",
  });

  const [resetDialog, setResetDialog] = useState(false);
  useEffect(() => {
    (async () => {
      if (trialId) {
        await getTrialDetailsV2({
          id: trialId,
          sponsorId: sponsorId,
        });
      }
    })();
  }, [dispatch]);

  const openMessageNotification = (message) => {
    dispatch(openMessage({message:message.message,messageSeverity:message.type}))
  };


  const handleConfirmDialogClose = () => {
    setConfirmDialog({
      ...confirmDialog,
      open: false,
      message: "",
      buttonLabel: "Save",
    });
  };

  const handleCancelDialogClose = () => {
    setCancelDialog({ ...cancelDialog, open: false })
  }

  const handleResetDialogClose = () => {
    setResetDialog(false);
  };

  const handleReset = () => {
    let documentsValidation = false;
    if(!formik.values.documents){
      documentsValidation = true;
    }
    setResetDialog(false);
    formik.handleReset();
    trialDetailsFormik.handleReset();
    if(documentsValidation){
      handleDocumentValidation(false)
    }
  };

  const trialDetailsValidation = Yup.object().shape({
    protocolNumber: Yup.string()
      .required("Protocol Number is required")
      .trim("The Protocol Number cannot include leading and trailing spaces")
      .strict(true)
      .min(2, "Minimum 2 characters are required")
      .max(50, "Maximum 50 characters are allowed"),
    protocolVersion: Yup.string()
      .required("Protocol Version is required")
      .trim("The Protocol Version cannot include leading and trailing spaces")
      .strict(true)
      .min(2, "Minimum 2 characters are required")
      .max(50, "Maximum 50 characters are allowed"),
    protocolVersionDate:Yup.date()
      .required("Protocol Version Date is required"),
    trialTitle: Yup.string()
      .required("Study Title is Required")
      .trim("The Study Title cannot include leading and trailing spaces")
      .strict(true)
      .min(2, "Minimum 2 characters are required"),
    totalSubjectNumber: Yup.number()
      .required("Subject number is Required")
      .min(0, "Number of subject cannot be negative")
      .max(1000000, "Total subjects cannot be more than 1000000"),
    siteInitiationForm: Yup.string().required(
      "Site Initiation Configuration is required"
    ),
    eConsentForm: Yup.string().required(
      "eConsent Configuration is required"
    ),
    crfForm: Yup.string().required(
      "crfForm Configuration is required"
    ),
  });

  const validation = Yup.object().shape({
    documents: Yup.boolean()
      .required("Documents are required")
      .test(
        "is-mandatory",
        "Documents are required",
        (value) => value === false
      ),
  });

  let initialValues = useMemo(() => {
    const trialData = trialDetailsV2?.trialData;
    return {
      documents: "",
      studyDetail: trialData?.studyDetail || {},
    };
  }, [trialDetailsV2]);

  let trialDetailsInitialValues = useMemo(() => {
    const trialData = trialDetailsV2?.trialData;
    return {
      protocolNumber: trialData?.protocolNumber || '',
      protocolVersion: trialData?.protocolVersion || '',
      protocolVersionDate: trialData?.protocolVersionDate
        ? moment(trialData.protocolVersionDate).format('MM-DD-YYYY')
        : moment().format('MM-DD-YYYY'),
      siteInitiationForm: trialData?.siteInitiationForm || '',
      eConsentForm: trialData?.eConsentForm || '',
      crfForm: trialData?.crfForm || '',
      trialTitle: trialData?.trialTitle || '',
      totalSubjectNumber: trialData?.totalSubjectNumber || '',
    };
  }, [trialDetailsV2]);

  const formik = useFormik({
    validateOnMount: true,
    initialValues,
    validationSchema: validation,
    enableReinitialize: true,
    onSubmit: (values) => {
      return values;
    },
  });

  const trialDetailsFormik = useFormik({
    validateOnMount: true,
    initialValues: trialDetailsInitialValues,
    validationSchema: trialDetailsValidation,
    enableReinitialize: true,
    onSubmit: (values) => {
      return values;
    },
  });

  useEffect(() => {
    trialDetailsV2 &&
    trialDetailsV2?.trialData?.studyDetail &&
      isValidTab(true);
  }, [trialDetailsV2]);

  useEffect(() => {
    if (trialDetailsV2) {
      setFieldsVisible(true);
    }
  }, [trialDetailsV2]);

  const handleSave = async (e) => {
    e.preventDefault();
    setConfirmDialog((prevState) => ({
      ...prevState,
      open: false,
      message: "",
      buttonLabel: "Save",
    }));
    if (fieldsVisible === false) {
      trialDetailsFormik.handleSubmit();
      if (trialDetailsFormik.isValid) {
        const result = await createTrialV2({
          trialPayload: trialDetailsFormik.values,
          sponsorId: sponsorId,
        });
        if (result.data) {
          navigate(`/createTrial/${result.data.id}`);
          await getTrialDetailsV2({
            id: result.data.id,
            sponsorId: sponsorId,
          });
        } else if (result.error) {
          const error =
            result.error.data.message.includes("protocolNumber") ||
            result.error.data.message.includes("protocol_number");
          return error
            ? trialDetailsFormik.setFieldError(
              "protocolNumber",
              "Protocol Number shuold be unique"
            )
            : null;
        }
      } else {
        openMessageNotification({
          message: "Check if all fields are valid!",
          type: "warning",
        });
      }
    } else {
      trialDetailsFormik.handleSubmit();
      formik.handleSubmit();
      if (
        (trialDetailsFormik.dirty || formik.dirty) &&
        trialDetailsFormik.isValid &&
        formik.isValid
      ) {
        const body = { ...trialDetailsFormik.values, ...formik.values };
        if (!body.studyDetail?.id) {
          const result = await createTrialPatchV2({
            id: trialId,
            sponsorId: sponsorId,
            trialPayload: body,
          });
          if (result.data) {
            openMessageNotification({
              message: "Submitted Successfully",
              type: "success",
            });
            isValidTab(true);
            handleTabChange(e, 1);
          } else if (result.error) {
            if (
              result.error.data.message.includes("account_number") &&
              result.error.data.message.includes("duplicate values")
            ) {
              openMessageNotification({
                message: "Account Number should be unique",
                type: "error",
              });
              formik.setFieldError(
                "bankDetail.accountNumber",
                "Account Number should be unique"
              );
            } else {
              openMessageNotification({
                message: "Unable to Submit",
                type: "error",
              });
            }
          }
        } else if (trialDetailsFormik.dirty && !formik.dirty) {
          //Update Trial Detail
          const result = await updateTrialPatch({
            id: trialId,
            sponsorId: sponsorId,
            trialPayload: body,
          });
          if (result.data) {
            openMessageNotification({
              message: "Submitted Successfully",
              type: "success",
            });
            handleTabChange(e, 1);
          } else if (result.error) {
            trialDetailsFormik.setFieldError(
              "protocol_number",
              "Duplicate Value"
            );
            openMessageNotification({
              message: "Duplicate Protocol Number",
              type: "error",
            });
          }
        } else if (!trialDetailsFormik.dirty && !formik.dirty) {
          openMessageNotification({
            message: "Submitted Successfully",
            type: "success",
          });
          handleTabChange(e, 1);
        } else {
          //Show Message to save individually
          openMessageNotification({
            message: "Please Update the Section Individually",
            type: "warning",
          });
        }
      } else {
        openMessageNotification({
          message: "Check if all fields are valid!",
          type: "warning",
        });
      }
    }
  };

  const handleCancel = () => {
    setCancelDialog({ ...cancelDialog, open: false })
    navigate('/')
  }

  const handleSubmit = () => {
    if (!fieldsVisible) {
      if (trialDetailsFormik.isValid) {
        setConfirmDialog({
          ...confirmDialog,
          open: true,
          message: "Do you want to save this form?",
          buttonLabel: "Save",
        });
      } else {
        setConfirmDialog({
          ...confirmDialog,
          open: true,
          message: "Validate all fields before submission",
          buttonLabel: "Ok",
        });
      }
    } else {
      if (trialDetailsFormik.isValid && formik.isValid) {
        setConfirmDialog({
          ...confirmDialog,
          open: true,
          message: "Do you want to save this form?",
          buttonLabel: "Save",
        });
      } else if (trialDetailsFormik.isValid && !formik.isValid) {
        setConfirmDialog({
          ...confirmDialog,
          open: true,
          message: "Validate all fields before submission",
          buttonLabel: "Ok",
        });
      } else {
        setConfirmDialog({
          ...confirmDialog,
          open: true,
          message: "Validate all fields before submission",
          buttonLabel: "Ok",
        });
      }
    }
  };

  const handleDocumentValidation = (value) => {
    formik.setFieldValue("documents", value);
  };
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, []);
  return (
    <Formik>
      <>
        <Grid container p={3} rowGap={2}>
          <Grid item xs={12}>
            <TrialDetailsSection
              formik={trialDetailsFormik}
              theraeupticAgents={theraeupticAgents}
              openMessageNotification={openMessageNotification}
              trialId={trialId}
              sponsorId={sponsorId}
            />
          </Grid>
          {fieldsVisible && (
            <>
              <Grid item xs={12}>
                <DocumentsSection
                  trialId={trialId}
                  touchedState={formik.touched.documents}
                  errorState={formik.errors.documents}
                  handleDocumentValidation={(value) => handleDocumentValidation(value)}
                  BoxName={'documents'}
                />
              </Grid>
              <Grid item xs={12}>
                <StudyDetailsSection
                  testArticles={testArticles}
                  trialDetails={trialDetailsV2}
                  formik={formik}
                  openMessageNotification={openMessageNotification}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button
              variant="outlined"
              onClick={() => setCancelDialog({ ...cancelDialog, open: true })}
              color="primary"
              size="large"
              sx={{ padding: '8px 30px', textTransform: 'none' }}
            >
              Cancel
            </Button>

            <Box>
              <Button
                variant="outlined"
                onClick={() => setResetDialog(true)}
                color="primary"
                sx={{ padding: '8px 30px', textTransform: 'none' }}
              >
                Reset
              </Button>
              <CustomButton onClick={handleSubmit} style={{ marginLeft: 15 }}>
                Save & Continue
              </CustomButton>
            </Box>
          </Grid>
        </Grid>
        <ConfirmationDialog
          open={confirmDialog.open}
          buttonLabel={confirmDialog.buttonLabel}
          message={confirmDialog.message}
          handleClose={handleConfirmDialogClose}
          handleConfirm={handleSave}
        />
        <ConfirmationDialog
          open={resetDialog}
          buttonLabel={'Reset'}
          message={'Do you want to reset this form?'}
          handleClose={handleResetDialogClose}
          handleConfirm={handleReset}
        />

        <ConfirmationDialog
          open={cancelDialog.open}
          buttonLabel={cancelDialog.buttonLabel}
          message={cancelDialog.message}
          handleClose={handleCancelDialogClose}
          handleConfirm={handleCancel}
        />
        <FormikErrorFocus
          // See scroll-to-element for configuration options: https://www.npmjs.com/package/scroll-to-element
          offset={0}
          align={'top'}
          focusDelay={200}
          ease={'linear'}
          duration={1000}
        />
      </>
    </Formik>
  );
};

export default BasicDetailsTab;
